import { useMemo, useSyncExternalStore } from 'react'
import { isAdmin, hasPermission, getRoleLevel, getRole, subscribeAuth, getAuthSnapshot } from '@/utils/auth'

export type ResourceType =
  | 'device' | 'product' | 'alert' | 'log' | 'debug'
  | 'forward' | 'dashboard' | 'user' | 'role' | 'tenant'
  | 'driver' | 'ota' | 'org'

export type ActionType = 'create' | 'read' | 'update' | 'delete' | 'execute' | 'assign_role' | 'read_secret'

export interface PermissionSet {
  isAdmin: boolean
  role: string
  roleLevel: number
  canRead: boolean
  canCreate: boolean
  canUpdate: boolean
  canDelete: boolean
  canExecute: boolean
  has: (action: ActionType) => boolean
}

export function usePermission (resource: ResourceType): PermissionSet {
  const snapshot = useSyncExternalStore(subscribeAuth, getAuthSnapshot)
  return useMemo(() => {
    const admin = isAdmin()
    const role = getRole()
    const roleLevel = getRoleLevel()

    const has = (action: ActionType) => hasPermission(resource, action)

    return {
      isAdmin: admin,
      role,
      roleLevel,
      canRead: has('read'),
      canCreate: has('create'),
      canUpdate: has('update'),
      canDelete: has('delete'),
      canExecute: has('execute'),
      has,
    }
  }, [resource, snapshot])
}

export function useMultiPermission (resources: ResourceType[]): Record<ResourceType, PermissionSet> {
  const snapshot = useSyncExternalStore(subscribeAuth, getAuthSnapshot)
  return useMemo(() => {
    const admin = isAdmin()
    const role = getRole()
    const roleLevel = getRoleLevel()
    const result: Partial<Record<ResourceType, PermissionSet>> = {}

    for (const resource of resources) {
      const has = (action: ActionType) => hasPermission(resource, action)
      result[resource] = {
        isAdmin: admin,
        role,
        roleLevel,
        canRead: has('read'),
        canCreate: has('create'),
        canUpdate: has('update'),
        canDelete: has('delete'),
        canExecute: has('execute'),
        has,
      }
    }
    return result as Record<ResourceType, PermissionSet>
  }, [resources, snapshot])
}
